// @ts-check
/**
 * The AI side panel's chrome: header, a scrolling body and a footer. Presentation only - everything it shows
 * arrives as props, so any AI feature can supply its own content without touching the shell.
 *
 * Rendered inside AIPanelProvider, which supplies the overlay, focus handling and dismissal.
 */
import React, { useId } from 'react';
import PropTypes from 'prop-types';
import { LuSparkles, LuX } from 'react-icons/lu';

import TextPlaceholder from '@components/general/TextPlaceholder';
import { TestableText } from '@components/general/TestableComponents';

export const DEFAULT_PROVENANCE = 'AI generated explanations based only on reporting data from metriQ';

/**
 * @typedef {Object} AIPanelProps
 * @property {string} title
 * @property {import('react').ReactNode} [provenance] - Standing provenance statement, shown in every state.
 * @property {import('react').ReactNode} [contextLine] - Supplied by the feature, e.g. organisation and project count.
 * @property {'loading'|'ready'|'error'} status - 'loading' shows the progress text over skeleton lines.
 * @property {string} [progressText]
 * @property {string} [announcement] - Read out when loading finishes. Not shown.
 * @property {import('react').ReactNode} [headerActions]
 * @property {import('react').ReactNode} [footer]
 * @property {() => void} onClose
 * @property {import('react').ReactNode} [children]
 */

/** @param {AIPanelProps} props */
const AIPanel = ({
  title,
  provenance = DEFAULT_PROVENANCE,
  contextLine = null,
  status,
  progressText = '',
  announcement = '',
  headerActions = null,
  footer = null,
  onClose,
  children = null,
}) => {
  const titleId = useId();
  const isLoading = status === 'loading';

  return (
    <section
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-busy={isLoading}
      tabIndex={-1}
      className="flex h-full flex-col bg-white shadow-xl outline-none dark:bg-dark-tremor-background-muted"
      data-testid="ai-panel"
    >
      <header className="border-b border-gray-200 px-5 py-4 dark:border-gray-700">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-tremor-small bg-tremor-background-subtle text-tremor-brand dark:bg-gray-700 dark:text-dark-tremor-brand"
              >
                <LuSparkles size={16} />
              </span>
              <h2 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-gray-100" data-testid="ai-panel-title">
                {title}
              </h2>
            </div>
            {/* Standing provenance: shown in every state, loading included. */}
            <TestableText variant="small" className="mt-1 text-sm text-gray-600 dark:text-gray-300" dataTestId="ai-panel-provenance">
              {provenance}
            </TestableText>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {headerActions}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded p-1.5 text-gray-600 hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-tremor-brand dark:text-gray-300 dark:hover:bg-gray-700"
              data-testid="ai-panel-close"
            >
              <LuX aria-hidden="true" size={20} />
            </button>
          </div>
        </div>
      </header>

      {/* What the report is about, kept out of the header so it can change without the chrome moving. */}
      {contextLine && (
        <div
          className="border-b border-gray-200 px-5 py-3 dark:border-gray-700"
          data-testid="ai-panel-context"
        >
          {contextLine}
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-5 py-4" data-testid="ai-panel-body">
        {/*
          Always mounted, so a screen reader hears the change: the progress text while loading, then the
          announcement when the content arrives.
        */}
        <TestableText
          variant="small"
          role="status"
          className={isLoading ? 'mb-4 text-sm text-gray-700 dark:text-gray-300' : 'sr-only'}
          dataTestId="ai-panel-status"
        >
          {isLoading ? progressText : announcement}
        </TestableText>

        {isLoading ? (
          <div aria-hidden="true" data-testid="ai-panel-skeleton">
            <TextPlaceholder lines={6} width="w-full" />
          </div>
        ) : (
          children
        )}
      </div>

      {footer && (
        <footer className="border-t border-gray-200 px-5 py-3 dark:border-gray-700" data-testid="ai-panel-footer">
          {footer}
        </footer>
      )}
    </section>
  );
};

AIPanel.propTypes = {
  title: PropTypes.string.isRequired,
  provenance: PropTypes.node,
  contextLine: PropTypes.node,
  status: PropTypes.oneOf(['loading', 'ready', 'error']).isRequired,
  progressText: PropTypes.string,
  announcement: PropTypes.string,
  headerActions: PropTypes.node,
  footer: PropTypes.node,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node,
};

export default AIPanel;
